"use client";

import { Icon3dPackage, IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconAlertInfoOutline18, IconMinusOutline18, IconPlusOutline18 } from "nucleo-ui-outline-18";
import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useCallback, useEffect, useRef, useState } from "react";
import type { TriggerArchetypeProps } from "./types";

// ============================================================================
// Types
// ============================================================================

interface ShopifyVariant {
  id: string;
  title: string;
  price: string;
  sku: string | null;
  inventoryQuantity: number;
  inventoryItemId: string;
}
interface ShopifyProduct {
  id: string;
  title: string;
  handle: string;
  status: string;
  vendor: string;
  productType: string;
  variants: ShopifyVariant[];
}

// ============================================================================
// Helpers
// ============================================================================

async function searchProducts(connectorId: string, query?: string, signal?: AbortSignal): Promise<ShopifyProduct[]> {
  const inputs: Record<string, unknown> = {
    status: "ACTIVE",
    limit: 10
  };
  if (query) inputs.title = query;
  const response = await authenticatedFetch<{
    data?: {
      result?: {
        success: boolean;
        data?: {
          products?: ShopifyProduct[];
        };
        error?: {
          message: string;
        };
      };
    };
  }>(`${V2_ENDPOINT}/v1/actions/shopify-search-products/test`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      inputs,
      connector: {
        id: connectorId,
        type: "shopify"
      }
    }),
    signal: signal ?? AbortSignal.timeout(30_000)
  });
  if (!response.ok) throw new Error(`Failed to fetch products (HTTP ${response.status}).`);
  const json = await response.json();
  const result = json.data?.result;
  if (result && !result.success) {
    throw new Error(result.error?.message ?? "Shopify action failed. Check connector scopes.");
  }
  return result?.data?.products ?? [];
}

// ============================================================================
// Component
// ============================================================================

/**
 * ShopifyInventoryAdjust archetype — search for a product, pick a variant,
 * adjust the inventory quantity, and simulate an inventory_levels/update event.
 */
export function ShopifyInventoryAdjust({
  triggerPayload,
  onRun,
  connectorId
}: TriggerArchetypeProps) {
  // Search state
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Selection state
  const [selectedProduct, setSelectedProduct] = useState<ShopifyProduct | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<ShopifyVariant | null>(null);

  // Adjustment state
  const [delta, setDelta] = useState(0);
  const shopDomain = String(triggerPayload.shopDomain ?? "");

  // Load products
  const loadProducts = useCallback(async (searchQuery?: string) => {
    if (!connectorId) {
      setError("No Shopify store connected. Add a Shopify connector to test this trigger.");
      setIsLoading(false);
      return;
    }
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsLoading(true);
    setError(null);
    try {
      const results = await searchProducts(connectorId, searchQuery, controller.signal);
      setProducts(results);
      if (results.length === 0) {
        setError(searchQuery ? "No products found matching your search." : "No products found in your Shopify store.");
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      setError(err instanceof Error ? err.message : "Failed to fetch products.");
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  }, [connectorId]);

  // Debounced search (also handles initial load via query="" on mount)
  const isFirstLoad = useRef(true);
  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      loadProducts(query || undefined);
      return;
    }
    debounceRef.current = setTimeout(() => {
      loadProducts(query || undefined);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, loadProducts]);

  // Build payload and notify parent when selection or delta changes
  useEffect(() => {
    if (!selectedVariant) return;
    const newQuantity = Math.max(0, (selectedVariant.inventoryQuantity ?? 0) + delta);
    const now = new Date().toISOString();
    const inventoryItemId = selectedVariant.inventoryItemId ?? selectedVariant.id;
    if (!selectedVariant.inventoryItemId) {
      console.warn("[ShopifyInventoryAdjust] variant missing inventoryItemId, falling back to variant.id");
    }
    onRun({
      triggeredAt: now,
      topic: "inventory_levels/update",
      shopDomain,
      inventoryLevel: {
        inventory_item_id: inventoryItemId,
        available: newQuantity,
        updated_at: now
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onRun intentionally omitted to avoid re-firing on parent re-render
  }, [selectedVariant, delta, shopDomain]);

  // Select a product — auto-select its first variant
  const handleSelectProduct = (product: ShopifyProduct) => {
    setSelectedProduct(product);
    const firstVariant = product.variants?.[0] ?? null;
    setSelectedVariant(firstVariant);
    setDelta(0);
  };

  // Select a variant
  const handleSelectVariant = (variant: ShopifyVariant) => {
    setSelectedVariant(variant);
    setDelta(0);
  };
  const currentQuantity = selectedVariant?.inventoryQuantity ?? 0;
  const newQuantity = Math.max(0, currentQuantity + delta);
  return <div className="space-y-4">
      {/* Search input */}
      <div className="relative">
        <IconMagnifyingGlass className="text-ds-text-disabled absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
        <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search products by name..." className="border-ds-border-default text-ds-text-primary bg-ds-surface-card w-full rounded-lg border py-2 pl-9 pr-3 text-sm placeholder-zinc-400 outline-none transition-colors focus:border-zinc-400 dark:placeholder-zinc-500 dark:focus:border-zinc-500" />
      </div>
      {/* Loading */}
      {isLoading && <div className="flex items-center justify-center gap-2 py-6">
          <PixelLoader size="sm" color="blue" />
          <span className="text-ds-text-muted text-sm">Loading products...</span>
        </div>}
      {/* Error */}
      {error && !isLoading && products.length === 0 && <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2.5 dark:bg-amber-900/20">
          <IconAlertInfoOutline18 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <span className="text-xs text-amber-700 dark:text-amber-300">{error}</span>
        </div>}
      {/* Product list — shown when no product is selected */}
      {!isLoading && !selectedProduct && products.length > 0 && <div className="space-y-1">
          <span className="label-xs">
            {products.length} product{products.length !== 1 ? "s" : ""}
          </span>
          <div className="divide-ds-neutral-100 dark:divide-ds-neutral-800 divide-y">
            {products.map(product => <button key={product.id} type="button" onClick={() => handleSelectProduct(product)} className="dark:hover:bg-ds-neutral-800/60 hover:bg-ds-surface-hover flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors">
                <Icon3dPackage className="text-ds-text-disabled h-4 w-4 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="text-ds-text-primary text-sm font-medium">{product.title}</span>
                  <span className="text-ds-text-disabled dark:text-ds-text-muted ml-2 text-xs">
                    {product.variants.length} variant{product.variants.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </button>)}
          </div>
        </div>}
      {/* Selected product — variant picker + adjustment */}
      {selectedProduct && <div className="space-y-3">
          {/* Back button + product name */}
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => {
          setSelectedProduct(null);
          setSelectedVariant(null);
          setDelta(0);
        }} className="text-ds-text-disabled hover:text-ds-text-subtle text-xs transition-colors dark:hover:text-zinc-300">
              &larr; Back
            </button>
            <span className="text-ds-text-primary text-sm font-medium">{selectedProduct.title}</span>
          </div>

          {/* Variant selector — shown when product has multiple variants */}
          {selectedProduct.variants.length > 1 && <div className="space-y-1">
              <span className="label-xs">Select variant</span>
              <div className="divide-ds-neutral-100 border-ds-border-default dark:divide-ds-neutral-800 divide-y rounded-lg border">
                {selectedProduct.variants.map(variant => {
            const isSelected = selectedVariant?.id === variant.id;
            return <button key={variant.id} type="button" onClick={() => handleSelectVariant(variant)} className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors ${isSelected ? "bg-ds-neutral-100 dark:bg-ds-neutral-800" : "dark:hover:bg-ds-neutral-800/40 hover:bg-ds-surface-hover"}`}>
                      <div className="min-w-0">
                        <span className="text-ds-text-primary font-medium">
                          {variant.title === "Default Title" ? "Default" : variant.title}
                        </span>
                        {variant.sku && <span className="text-ds-text-disabled dark:text-ds-text-muted ml-2 text-xs">
                            SKU: {variant.sku}
                          </span>}
                      </div>
                      <span className="text-ds-text-muted ml-2 flex-shrink-0 text-xs tabular-nums">
                        {variant.inventoryQuantity} in stock
                      </span>
                    </button>;
          })}
              </div>
            </div>}

          {/* Inventory adjustment */}
          {selectedVariant && <div className="border-ds-border-default space-y-3 rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <span className="label label-xs">Current stock</span>
                <span className="body-sm-medium text-ds-text-primary tabular-nums">{currentQuantity}</span>
              </div>

              {/* Delta input */}
              <div className="flex items-center justify-between">
                <span className="label label-xs">Adjustment</span>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setDelta(d => d - 1)} className="border-ds-border-default text-ds-text-muted hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800 flex h-7 w-7 items-center justify-center rounded-md border transition-colors">
                    <IconMinusOutline18 className="h-3 w-3" />
                  </button>
                  <input type="number" value={delta} onChange={e => {
              const n = parseInt(e.target.value, 10);
              setDelta(isNaN(n) ? 0 : n);
            }} className="border-ds-border-default text-ds-text-primary bg-ds-surface-card w-16 rounded-md border px-2 py-1 text-center text-sm tabular-nums outline-none focus:border-zinc-400 dark:focus:border-zinc-500" />
                  <button type="button" onClick={() => setDelta(d => d + 1)} className="border-ds-border-default text-ds-text-muted hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800 flex h-7 w-7 items-center justify-center rounded-md border transition-colors">
                    <IconPlusOutline18 className="h-3 w-3" />
                  </button>
                </div>
              </div>

              {/* New quantity preview */}
              <div className="border-ds-neutral-100 dark:border-ds-neutral-800 flex items-center justify-between border-t pt-3">
                <span className="label label-xs">New stock</span>
                <span className={`body-sm-medium tabular-nums ${delta > 0 ? "text-emerald-600 dark:text-emerald-400" : delta < 0 ? "text-red-500 dark:text-red-400" : "text-ds-text-primary"}`}>
                  {newQuantity}
                  {delta !== 0 && <span className="text-ds-text-disabled ml-1 text-xs font-normal">
                      ({delta > 0 ? "+" : ""}
                      {delta})
                    </span>}
                </span>
              </div>
            </div>}
        </div>}
    </div>;
}